/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useQuery } from '@apollo/client'
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  GET_LABELING_STATISTICS,
  UltraLabelingStatisticsQuery,
  UltraLabelingStatisticsQueryVariables,
  UltraStatsBucketSize,
  UltraStatsGranularity,
} from '@dai/graphql'
import { format, isValid, subDays } from 'date-fns'
import { useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import { Helmet } from 'react-helmet-async'

import useT from '@/i18n/useT'

const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const

type HistoricalMetric = {
  bucketSize: UltraStatsBucketSize
  series: Array<{
    email: string
    results: Array<{
      dateString: string
      count: number
    }>
  }>
}

type HistoricalChartData = {
  bucketSize: UltraStatsBucketSize
  categories: string[]
  series: Array<{ name: string; data: number[] }>
}

type WorkflowChart = {
  title: string
  chartData: HistoricalChartData
}

const CHART_SERIES_COLORS = [
  '#2196f3',
  '#26a69a',
  '#ff9800',
  '#ef5350',
  '#8e24aa',
  '#5c6bc0',
  '#ec407a',
  '#66bb6a',
] as const

const getMonthLabel = (month: string, monthLabels: string[]) => {
  const monthIndex = Number(month) - 1

  if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return month
  }

  return monthLabels[monthIndex]
}

const formatAxisDateLabel = (
  value: string | number | undefined,
  bucketSize: UltraStatsBucketSize,
  monthLabels: string[],
) => {
  if (typeof value !== 'string' || !value) {
    return ''
  }

  const parts = value.split('-')

  if (bucketSize === UltraStatsBucketSize.DAILY && parts.length === 3) {
    const [, month, day] = parts
    return `${getMonthLabel(month, monthLabels)} ${Number(day)}`
  }

  if (bucketSize === UltraStatsBucketSize.MONTHLY && parts.length >= 2) {
    const [year, month] = parts
    return `${getMonthLabel(month, monthLabels)} ${year}`
  }

  return value
}

const formatTooltipDateLabel = (
  value: string | number | undefined,
  bucketSize: UltraStatsBucketSize,
  monthLabels: string[],
) => {
  if (typeof value !== 'string' || !value) {
    return ''
  }

  const parts = value.split('-')

  if (bucketSize === UltraStatsBucketSize.DAILY && parts.length === 3) {
    const [year, month, day] = parts
    return `${getMonthLabel(month, monthLabels)} ${Number(day)}, ${year}`
  }

  if (bucketSize === UltraStatsBucketSize.MONTHLY && parts.length >= 2) {
    const [year, month] = parts
    return `${getMonthLabel(month, monthLabels)} ${year}`
  }

  return value
}

const compareDateStrings = (left: string, right: string) =>
  new Date(left).getTime() - new Date(right).getTime()

const toDateInputValue = (value: Date | null) =>
  value && isValid(value) ? format(value, 'yyyy-MM-dd') : null

const buildHistoricalChartData = (
  metric: HistoricalMetric,
): HistoricalChartData => {
  const categories = Array.from(
    new Set(
      metric.series.flatMap(({ results }) =>
        results.map(({ dateString }) => dateString),
      ),
    ),
  ).sort(compareDateStrings)

  const series = metric.series.map(({ email, results }) => {
    const countsByDate = new Map(
      results.map(({ dateString, count }) => [dateString, count]),
    )

    return {
      name: email,
      data: categories.map(category => countsByDate.get(category) ?? 0),
    }
  })

  return {
    bucketSize: metric.bucketSize,
    categories,
    series,
  }
}

const getChartsForTab = (
  stats: UltraLabelingStatisticsQuery['labelingStatistics'] | undefined,
  tab: number,
  t: ReturnType<typeof useT>['t'],
): WorkflowChart[] => {
  if (!stats) return []

  switch (tab) {
    case 0:
      return stats.classification
        ? [
            {
              title: t(
                'stats.classification.batches.title',
                'Classification Batches',
              ),
              chartData: buildHistoricalChartData(stats.classification.batches),
            },
            {
              title: t(
                'stats.classification.crops.title',
                'Classification Crops',
              ),
              chartData: buildHistoricalChartData(stats.classification.crops),
            },
          ]
        : []
    case 1:
      return stats.detection
        ? [
            {
              title: t('stats.detection.boxes.title', 'Detection Boxes'),
              chartData: buildHistoricalChartData(stats.detection.boxes),
            },
            {
              title: t('stats.detection.images.title', 'Detection Images'),
              chartData: buildHistoricalChartData(stats.detection.images),
            },
          ]
        : []
    case 2:
      return stats.correction
        ? [
            {
              title: t('stats.correction.boxes.title', 'Correction Boxes'),
              chartData: buildHistoricalChartData(stats.correction.corrections),
            },
          ]
        : []
    default:
      return []
  }
}

function HistoricalChart({
  title,
  chartData,
  isDark,
  foreColor,
  monthLabels,
}: {
  title: string
  chartData: HistoricalChartData
  isDark: boolean
  foreColor: string
  monthLabels: string[]
}) {
  const { t } = useT()
  const subtitle =
    chartData.bucketSize === UltraStatsBucketSize.DAILY
      ? t('stats.chart.daily', 'Daily')
      : t('stats.chart.monthly', 'Monthly')
  const seriesColors = chartData.series.map(
    (_, index) => CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length],
  )

  const options = {
    chart: {
      stacked: true,
      toolbar: { show: false },
      background: 'transparent',
      foreColor,
    },
    grid: {
      padding: {
        right: 20,
      },
    },
    theme: { mode: isDark ? ('dark' as const) : ('light' as const) },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 2,
      },
    },
    dataLabels: { enabled: false },
    colors: seriesColors,
    legend: { show: false },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (_value: number, opts?: { dataPointIndex: number }) =>
          formatTooltipDateLabel(
            typeof opts?.dataPointIndex === 'number'
              ? chartData.categories[opts.dataPointIndex]
              : undefined,
            chartData.bucketSize,
            monthLabels,
          ),
      },
    },
    noData: { text: t('stats.chart.noData', 'No data available.') },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: -45,
        trim: true,
        minHeight: 60,
        maxHeight: 60,
        formatter: (value: string | number | undefined) =>
          formatAxisDateLabel(value, chartData.bucketSize, monthLabels),
      },
    },
    yaxis: {
      title: {
        text: t('stats.chart.count', 'Count'),
      },
      min: 0,
    },
  }

  return (
    <Stack spacing={1}>
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {chartData.series.length > 0 ? (
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          {chartData.series.map((series, index) => (
            <Stack
              key={series.name}
              direction="row"
              spacing={0.75}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: seriesColors[index],
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {series.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : null}
      {chartData.categories.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(
            'stats.chart.noHistoricalData',
            'No historical data available for this date range.',
          )}
        </Typography>
      ) : (
        <Chart
          type="bar"
          height={320}
          options={options}
          series={chartData.series}
        />
      )}
    </Stack>
  )
}

function StatsFilters({
  startDate,
  endDate,
  granularity,
  onStartDateChange,
  onEndDateChange,
  onGranularityChange,
}: {
  startDate: Date | null
  endDate: Date | null
  granularity: UltraStatsGranularity
  onStartDateChange: (value: Date | null) => void
  onEndDateChange: (value: Date | null) => void
  onGranularityChange: (value: UltraStatsGranularity) => void
}) {
  const { t } = useT()

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1">
            {t('stats.filters.title', 'Filters')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label={t('stats.filters.startDate', 'Start date')}
                value={startDate}
                maxDate={endDate ?? undefined}
                onChange={onStartDateChange}
                slots={{ textField: TextField }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label={t('stats.filters.endDate', 'End date')}
                value={endDate}
                minDate={startDate ?? undefined}
                onChange={onEndDateChange}
                slots={{ textField: TextField }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel id="stats-granularity-label">
                  {t('stats.filters.granularity', 'Granularity')}
                </InputLabel>
                <Select
                  labelId="stats-granularity-label"
                  value={granularity}
                  label={t('stats.filters.granularity', 'Granularity')}
                  onChange={event =>
                    onGranularityChange(
                      event.target.value as UltraStatsGranularity,
                    )
                  }
                >
                  <MenuItem value={UltraStatsGranularity.AUTO}>
                    {t('stats.filters.auto', 'Auto')}
                  </MenuItem>
                  <MenuItem value={UltraStatsGranularity.DAILY}>
                    {t('stats.filters.daily', 'Daily')}
                  </MenuItem>
                  <MenuItem value={UltraStatsGranularity.MONTHLY}>
                    {t('stats.filters.monthly', 'Monthly')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  )
}

function Stats() {
  const { t } = useT()
  const muiTheme = useTheme()
  const isDark = muiTheme.palette.mode === 'dark'
  const foreColor = muiTheme.palette.text.primary
  const today = useMemo(() => new Date(), [])

  const tabLabels = [
    t('stats.tabs.classification', 'Classification'),
    t('stats.tabs.detection', 'Detection'),
    t('stats.tabs.correction', 'Correction'),
  ]
  const monthLabels = MONTH_KEYS.map(month =>
    t(`stats.months.${month}`, month.charAt(0).toUpperCase() + month.slice(1)),
  )

  const [tab, setTab] = useState(0)
  const [startDate, setStartDate] = useState<Date | null>(subDays(today, 29))
  const [endDate, setEndDate] = useState<Date | null>(today)
  const [granularity, setGranularity] = useState<UltraStatsGranularity>(
    UltraStatsGranularity.AUTO,
  )

  const normalizedRange = useMemo(() => {
    if (startDate && endDate && startDate > endDate) {
      return {
        startDate: endDate,
        endDate: startDate,
      }
    }

    return { startDate, endDate }
  }, [endDate, startDate])

  const { data, loading, error } = useQuery<
    UltraLabelingStatisticsQuery,
    UltraLabelingStatisticsQueryVariables
  >(GET_LABELING_STATISTICS, {
    variables: {
      startDate: toDateInputValue(normalizedRange.startDate),
      endDate: toDateInputValue(normalizedRange.endDate),
      granularity,
    },
    context: { endPoint: 'mlUltra' },
  })

  const charts = getChartsForTab(data?.labelingStatistics, tab, t)

  return (
    <>
      <Helmet title={t('stats.helmet', 'Stats')} />
      <Stack spacing={4}>
        <StatsFilters
          startDate={startDate}
          endDate={endDate}
          granularity={granularity}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onGranularityChange={value => setGranularity(value)}
        />

        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          {tabLabels.map(label => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>

        <Grid container spacing={4}>
          {loading &&
            Array.from({ length: 2 }, (_, index) => (
              <Grid key={index} size={12}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" height={320} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          {error && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="error">
                    {t(
                      'stats.loadError',
                      'Failed to load labeling statistics.',
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {!loading &&
            !error &&
            charts.map(chart => (
              <Grid key={chart.title} size={12}>
                <Card>
                  <CardContent>
                    <HistoricalChart
                      title={chart.title}
                      chartData={chart.chartData}
                      isDark={isDark}
                      foreColor={foreColor}
                      monthLabels={monthLabels}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Stack>
    </>
  )
}

export default Stats
